import { mapGetters, mapMutations, mapState } from 'vuex';
import { isNull } from 'lodash';
import { maxAdditionalGridViewAttributes } from '@enums/feature-flags';

const mixin = {
  data: () => {
    return {
      gridApi: null,
      columnToolPanel: {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
        },
        width: 385,
      },
      additionalAttributes: new Set(),
    };
  },
  computed: {
    ...mapState('clientConfig', ['toggleLogic']),
    ...mapGetters('attributes', ['getGridDisplayedAttributes']),
  },
  methods: {
    ...mapMutations('notifications', ['addNotification']),
    /**
     * Handler for when the column tool panel is closed. Does nothing by default.
     * To be implemented by the component, if required.
     */
    handleColumnsPanelClosed() {},
    /**
     * Toggles the visibility of the selected tool panel.
     * @param {string} selectedToolPanel - The tool panel to toggle.
     */
    toggleSideBar(selectedToolPanel) {
      if (!this.gridApi) return;

      const openToolPanel = this.gridApi.getOpenedToolPanel();

      if (openToolPanel === 'columns') {
        this.handleColumnsPanelClosed();
      }

      // If the selected toolbar is open, close it, otherwise open it.
      if (openToolPanel === selectedToolPanel) {
        this.gridApi.setSideBarVisible(false);
        this.gridApi.closeToolPanel();
      } else {
        this.gridApi.setSideBarVisible(true);
        this.gridApi.openToolPanel(selectedToolPanel);
      }
    },
    /**
     * Hides attribute columns if the number of additional attributes exceeds the maximum allowed.
     */
    hideAttributeColumnsOverMaxCount() {
      const maxAttributes = this.toggleLogic[maxAdditionalGridViewAttributes];
      // Limit maximum displayed columns if needed
      if (this.additionalAttributes.size > maxAttributes) {
        this.additionalAttributes = new Set(
          Array.from(this.additionalAttributes).slice(0, maxAttributes)
        );

        const attributesGroup = this.gridApi.getColumnGroup('attributes');
        attributesGroup.getDisplayedLeafColumns().forEach(column => {
          const attributeKey = column.colId.split('attributes.')[1];
          const shouldShow = this.additionalAttributes.has(attributeKey);
          if (!shouldShow && column.visible) {
            this.gridApi.setColumnsVisible([column], shouldShow);
          }
        });

        this.addNotification({
          id: `gridViewMaxAdditionalAttributes:warning:${Date.now()}`,
          jobStatus: 'warning',
          baseTranslation: 'notifications.gridViewMaxAdditionalAttributes',
          notificationPayload: maxAttributes,
        });
      }
    },
    /**
     * Updates the set of additional attributes based on column visibility changes.
     * @param {Object[]} columns - The columns to process.
     */
    updateAdditionalAttributes(columns) {
      columns.forEach(column => {
        if (column.colId.startsWith('attributes.')) {
          const attributeKey = column.colId.split('attributes.')[1];
          if (column.visible) {
            this.additionalAttributes.add(attributeKey);
          } else {
            this.additionalAttributes.delete(attributeKey);
          }
        }
      });
    },
    /**
     * Returns the correct visibility object for attribute columns
     * - If activeLayout exists: let layout control visibility
     * - Otherwise: use the existing logic based on getGridDisplayedAttributes
     * @param {string} attributeKey - The attribute key to check
     * @param {Object} options - Additional boolean options
     * @param {boolean} options.isDefault - Whether this is a default attribute (affects the logic used)
     * @param {boolean} options.activeLayout - Whether there is an active layout (affects the logic used)
     */
    getAttributeColumnVisibilityState(
      attributeKey,
      { isDefault = false, activeLayout = false } = {}
    ) {
      if (activeLayout) {
        return {};
      }

      if (isDefault) {
        return {
          hide: isNull(this.getGridDisplayedAttributes)
            ? false
            : !this.getGridDisplayedAttributes.has(attributeKey),
        };
      }
      return {
        hide:
          isNull(this.getGridDisplayedAttributes) ||
          !this.getGridDisplayedAttributes.has(attributeKey),
      };
    },
  },
};

export default mixin;
